import useSWR from 'swr'
import { apiGetBillingSummary, apiGetPlans } from '@/services/BillingService'
import type { BillingSummary, PlanOption } from '@/@types/trackheros'

/**
 * The catalogue and the plan this account is already on — what anything that
 * offers a plan choice needs, and nothing else.
 *
 * Used by onboarding's plan step and the drawer's plan dialog. Same SWR keys as
 * the billing page's `useBilling`, so whichever loads first warms the others.
 * Invoices are deliberately not fetched here — they are a live Lago read, and
 * choosing a plan does not need them.
 *
 * Plans are our own `billing.Plan` rows, defined in the admin and synced to
 * Lago — and, on a Paddle deployment, to Paddle's catalogue. An empty list
 * means billing is not set up in this environment, and there is then no
 * choice to offer.
 *
 * The summary is admin-only, so for a member `unavailable` is true — which is
 * the right answer: a member cannot change the plan either.
 */
export default function usePlanCatalogue() {
    const plans = useSWR<{ plans: PlanOption[] }>(
        ['billing', 'plans'],
        () => apiGetPlans(),
        { revalidateOnFocus: false, shouldRetryOnError: false },
    )

    const summary = useSWR<BillingSummary>(
        ['billing', 'summary'],
        () => apiGetBillingSummary(),
        { revalidateOnFocus: false, shouldRetryOnError: false },
    )

    return {
        plans: plans.data?.plans ?? [],
        currentPlanCode: summary.data?.plan.code ?? '',
        hasCard: summary.data?.payment_method?.state === 'active',
        provider: summary.data?.provider?.provider ?? 'stripe',
        // Either request failing means we cannot offer a choice honestly;
        // treat it like "no plans" rather than blocking somebody's first
        // minute on a billing outage.
        isLoading: plans.isLoading || summary.isLoading,
        unavailable: Boolean(plans.error || summary.error),
    }
}
